import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import type {TabItemContentStorage, TabKey} from '@Components/tabs';
import {Tabs} from '@Components/tabs';
import {Dropdown} from '@Components/dropdown-v2';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {DESIGN_SELECTOR_DESIGNS_TAB_KEY, DesignSelectorTab, getDesignSelectorTopTabKey} from '@Components/design-selector/design-selector.types';
import {ClickableDiv} from '@Components/clickable-div';
import type {DesignSelectorTabSwitcherProps} from './design-selector-tab-switcher.types';
import {SELECTION_SUMMARY_SEPARATOR, TOP_TAB_UNPADDED_CLASSES} from './design-selector-tab-switcher.types';
import styles from './design-selector-tab-switcher.module.scss';
import {openTeamsUpsellDialog, TeamsUpsellSource} from '@Libraries/teams-upsell.library';

export function DesignSelectorTabSwitcher({
  designSelectorTab,
  onTabSelected,
  isMobile,
  hideTabContainer = false,
  tabsId,
  selectedDesignCount = 0,
  onDeselectAll,
}: DesignSelectorTabSwitcherProps): ReactElement | null {
  if (hideTabContainer) {
    return null;
  }

  const isMyDesignsSelected = designSelectorTab === DesignSelectorTab.MY_DESIGNS;
  const isTeamSelected = designSelectorTab === DesignSelectorTab.SHARED_WITH_ME;
  const isDesignGallerySelected = designSelectorTab === DesignSelectorTab.GALLERY_TEMPLATES;

  const onSharedWithMeTabSelected = (): void => {
    if (window.PMW.isPAYGUser()) {
      openTeamsUpsellDialog(TeamsUpsellSource.DESIGN_SELECTOR_SHARED_TAB, {shouldOpenInNewTab: true});
    } else {
      onTabSelected(DesignSelectorTab.SHARED_WITH_ME);
    }
  };

  const onTopTabClick = (tabKey: TabKey): void => {
    if (tabKey === DESIGN_SELECTOR_DESIGNS_TAB_KEY) {
      if (isDesignGallerySelected) {
        onTabSelected(DesignSelectorTab.MY_DESIGNS);
      }
      return;
    }

    onTabSelected(DesignSelectorTab.GALLERY_TEMPLATES);
  };

  const getTopTabItems = (): TabItemContentStorage[] => {
    return [
      {
        tabKey: DESIGN_SELECTOR_DESIGNS_TAB_KEY,
        text: window.i18next.t('pmwjs_designs'),
        containerClassName: 'spacing-m-r-6',
        textClassName: 'body-md-bold',
        className: TOP_TAB_UNPADDED_CLASSES,
      },
      {tabKey: DesignSelectorTab.GALLERY_TEMPLATES, text: window.i18next.t('pmwjs_templates_capitalized'), textClassName: 'body-md-bold', className: TOP_TAB_UNPADDED_CLASSES},
    ];
  };

  const getDropdownText = (): string => {
    if (isMyDesignsSelected) {
      return window.i18next.t('pmwjs_my_designs');
    }
    if (isTeamSelected) {
      return window.i18next.t('pmwjs_team');
    }
    return window.i18next.t('pmwjs_design_gallery');
  };

  const getMobileClasses = (): string => {
    return `${styles.upperContainerMobile} ${isDesignGallerySelected ? 'spacing-m-b-0' : ''}`;
  };

  const getSelectionSummary = (): ReactElement | null => {
    if (!onDeselectAll || selectedDesignCount === 0) {
      return null;
    }

    const deselectCtaText = selectedDesignCount === 1 ? window.i18next.t('pmwjs_deselect') : window.i18next.t('pmwjs_deselect_all');

    return (
      <div className={styles.selectionSummary}>
        <Text size={TextSize.MD} bold val={window.i18next.t('pmwjs_x_designs_selected', {count: selectedDesignCount})} className={styles.selectionSummaryCount} />
        <Text size={TextSize.MD} bold val={SELECTION_SUMMARY_SEPARATOR} className={styles.selectionSummaryCount} attributes={{'aria-hidden': 'true'}} />
        <ClickableDiv className={styles.selectionSummaryDeselectCta} onClick={onDeselectAll}>
          <Text size={TextSize.MD} bold underline val={deselectCtaText} className={styles.selectionSummaryDeselectText} />
        </ClickableDiv>
      </div>
    );
  };

  const getDesignSubTabRow = (): ReactElement | null => {
    if (isDesignGallerySelected) {
      return null;
    }

    return (
      <div className={styles.designSubTabRow}>
        <div className={styles.pillsRow}>
          <PillButton
            size={PillButtonSize.SMALL}
            textSize={TextSize.SM}
            text={window.i18next.t('pmwjs_my_designs')}
            isSelected={isMyDesignsSelected}
            onClick={(): void => {
              onTabSelected(DesignSelectorTab.MY_DESIGNS);
            }}
          />
          <PillButton
            size={PillButtonSize.SMALL}
            textSize={TextSize.SM}
            text={window.i18next.t('pmwjs_shared_with_me')}
            isSelected={isTeamSelected}
            onClick={onSharedWithMeTabSelected}
          />
        </div>
        {getSelectionSummary()}
      </div>
    );
  };

  const getDesktopUpperContainer = (): ReactElement => {
    return (
      <div id="design-selector-onboarding-container" className={styles.upperContainerDesktop}>
        <Tabs id={tabsId} className="spacing-p-l-0" tabItems={getTopTabItems()} selectedTab={getDesignSelectorTopTabKey(designSelectorTab)} onTabClick={onTopTabClick} />
        {getDesignSubTabRow()}
      </div>
    );
  };

  const getMobileUpperContainer = (): ReactElement => {
    return (
      <div className={getMobileClasses()}>
        <Text val={window.i18next.t('pmwjs_select_a_design_lowercase')} className={`${styles.selectADesignTextMobile} _unmargin`} />
        <Dropdown
          className={styles.designSelectorMobileDropdown}
          selector={<DropdownDefaultSelectBox id="design-selector-dropdown-1" text={getDropdownText()} size={DROPDOWN_SIZE.SMALL} isLoading={false} />}
          popup={
            <div style={{width: '100%'}}>
              <ControlledList
                id="design-selector-dropdown-list"
                items={[
                  {
                    id: 'design-selector-dropdown-2',
                    text: window.i18next.t('pmwjs_my_designs'),
                    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
                    isSelected: isMyDesignsSelected,
                    onClick: (): void => {
                      onTabSelected(DesignSelectorTab.MY_DESIGNS);
                    },
                    showSelectionCheckmark: false,
                  },
                  {
                    id: 'design-selector-dropdown-3',
                    text: window.i18next.t('pmwjs_team'),
                    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
                    onClick: onSharedWithMeTabSelected,
                    showSelectionCheckmark: false,
                  },
                  {
                    id: 'design-selector-dropdown-4',
                    text: window.i18next.t('pmwjs_design_gallery'),
                    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
                    onClick: (): void => {
                      onTabSelected(DesignSelectorTab.GALLERY_TEMPLATES);
                    },
                    showSelectionCheckmark: false,
                  },
                ]}
              />
            </div>
          }
        />
      </div>
    );
  };

  return isMobile ? getMobileUpperContainer() : getDesktopUpperContainer();
}
